import type { AxiosError } from "axios";
import axios from "axios";
import { useContext, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components/macro";
import { AccordionCardWithEditFeatures } from "../../../../components/AccordionCard/AccordionCardwithEditFeatures";
import { AttributeGroupDetails } from "../../../../components/AttributeGroupCard/AttributeGroupDetails";
import { AttributeGroupEditForm } from "../../../../components/AttributeGroupCard";
import { useAuthContext } from "../../../../components/Auth";
import { Notifications } from "../../../../components/Notifications/NotificationsContext";
import { endpoints } from "../../../../endpoints";
import type { DataMutate, UUID } from "../../../../types/types";
import type {
  AttributeGroupSummary,
  AttributeSchema,
  DocumentAttributeValue,
  ImageAttributeValue,
  PIMProduct,
  PIMProductPartial,
  PIMProductStatusAck,
} from "../../../../types/types.PIM";
import { useStoreState, useUpdateProductStatus } from "../../../../util/util";
import type { ProductGroupFormValues } from "./product_overview.utils";
import { getProductTypePermissionSuffix } from "./product_overview.utils";
import { buildAttributesPatchData } from "./attributePatch.utils";
import {
  isAttributeEditRejected,
  getAttributeEditRejectedMessage,
} from "../../../../util/attributeEditability";
import { Modal } from "../../../../components/Modal/Modal";
import { H4 } from "../../../../components/Typography/Typography";
import {
  applyPartialPimProductAndRefresh,
  revalidatePimProductDeferred,
} from "../../../../util/pimProductResponseMerge";
import { ObjectReferenceChangeConfirmDialog } from "../../../../components/ObjectReference/ObjectReferenceChangeConfirmDialog";
import {
  shouldConfirmObjectReferenceChange,
  shouldShowObjectReferenceAttribute,
} from "../../../../components/ObjectReference/utils";
import { ProductContext } from "../ProductContext";
import { isDocumentAttribute } from "../../../../components/AttributeGroupFormGenerator/DocumentAttribute/documentAsset.utils";
import { PROFILE_DETAIL_CLASSES } from "../../../../layout/shared/ProfileDetailLayout";
import { ChangeRequestFormModal } from "../../../../components/ChangeRequestForm/ChangeRequestModal";
import {
  ChangeRequestFormValues,
  resolveAttributeOptions,
  submitChangeRequest,
} from "../../../../components/ChangeRequestForm/changerequest.util";
import { isAttributeVisibleInCard } from "../../../admin/SellerAdmin/MDM/shared/utils";

interface IProductGroupCardProps {
  product: PIMProduct;
  isChangeRequestEnabled?: boolean;
  group: AttributeGroupSummary;
  mutateProduct: DataMutate<PIMProduct>;
  replaceProductId: (id: string) => void;
  key: string;
  isPortfolio?: boolean;
  editInModal?: boolean;
}

const EditFormWrapper = styled.div<{ editInModal?: boolean }>`
  margin-top: ${({ theme }) => theme.resolve("spacing_2xl")};
  ${({ editInModal }) =>
    editInModal &&
    css`
      div[class*="FormLayout__FormFieldAndIcon"] {
        grid-template-columns: 420px repeat(auto-fill, minmax(18px, auto)) !important;
      }
    `}
`;

const ProductGroupCardOuter = styled.div`
  min-width: 0;
  width: 100%;
`;

export const ProductGroupCard = ({
  product,
  group,
  mutateProduct,
  isPortfolio = false,
  replaceProductId,
  isChangeRequestEnabled = false,
  editInModal = false,
}: IProductGroupCardProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChangeRequest, setIsChangeRequest] = useState(false);
  const [pendingObjectRefConfirmation, setPendingObjectRefConfirmation] =
    useState<ProductGroupFormValues | null>(null);
  const {
    tenant_id,
    storefront_metadata: { object_reference_enabled },
  } = useStoreState();
  const { notifySuccess, notifyError } = useContext(Notifications);
  const { t } = useTranslation();
  const updateProductStatus = useUpdateProductStatus({ product });
  const {
    hasPermission,
    roleIsSomeKindOfSeller,
    roleIsGuest,
    roleIsSomeKindOfBuyer,
  } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const methodsOfUseForm = useForm();
  const productFromContext = useContext(ProductContext) ?? product;
  const resolvedGroup = useMemo(
    () =>
      productFromContext.product_schema.groups.find((g) => g.id === group.id) ??
      group,
    [group, productFromContext.product_schema.groups]
  );
  const productStatusBeforeEditRef = useRef(productFromContext.status);
  const productTypePermissionSuffix = getProductTypePermissionSuffix(
    product.pim_product_type
  );

  const onSubmitChangeRequest = async (formValues: ChangeRequestFormValues) => {
    setIsSubmitting(true);
    try {
      await submitChangeRequest({
        tenant_id,
        entity: { id: product.id, name: product.name },
        isProduct: true,
        context: changeRequestContext,
        formValues,
        onSuccess: () => setIsChangeRequest(false),
      });
      notifySuccess(t("Change request submitted successfully."));
    } catch (error) {
      console.error(error);
      notifyError(t("Failed to submit change request."));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Hide empty attributes for portfolio view or sellers who can't edit
  // Users need BOTH modify_products permission AND is_editable to see blank attributes
  // When product is locked during approval, users with modify_products permission should still see content
  const effectiveEditRights =
    product.is_editable ||
    (product.is_locked &&
      hasPermission("modify_products") &&
      hasPermission(`modify_${productTypePermissionSuffix}`));
  const shouldHideEmptyAttributes =
    isPortfolio ||
    (roleIsSomeKindOfSeller &&
      !(hasPermission("modify_products") && effectiveEditRights));

  const group_attributes_map = useMemo(() => {
    return resolvedGroup.attributes.reduce((acc, attr) => {
      acc[attr.id] = attr;
      return acc;
    }, {} as Record<string, AttributeSchema>);
  }, [resolvedGroup.attributes]);

  const editableAttributes = useMemo(
    () =>
      (resolvedGroup.attributes as AttributeSchema[]).filter(
        (attr) =>
          attr.is_visible &&
          shouldShowObjectReferenceAttribute({
            inputType: attr.input_type,
            objectReferenceEnabled: object_reference_enabled,
          })
      ),
    [resolvedGroup.attributes, object_reference_enabled]
  );

  const changeRequestAttributeOptions = useMemo(
    () =>
      resolveAttributeOptions(
        editableAttributes.filter((attr) =>
          isAttributeVisibleInCard(attr, roleIsGuest, roleIsSomeKindOfBuyer)
        )
      ),
    [editableAttributes, roleIsGuest, roleIsSomeKindOfBuyer]
  );

  const changeRequestContext = useMemo(
    () => ({
      variant: "group" as const,
      groupData: {
        groupName: group.display_name || group.name,
        groupId: group.id,
        entityName: product.name,
        attributeOptions: changeRequestAttributeOptions,
      },
    }),
    [group, product.name, changeRequestAttributeOptions]
  );

  if (!editableAttributes.length) {
    return null;
  }

  const hasObjectReferenceValueChanged = (
    formValues: ProductGroupFormValues
  ): boolean =>
    editableAttributes.some((attribute) => {
      if (
        attribute.input_type !== "object_reference" ||
        attribute.is_not_applicable
      ) {
        return false;
      }

      return shouldConfirmObjectReferenceChange({
        existingValue: attribute.values,
        nextFormValue: formValues[attribute.id],
      });
    });

  const updateApplicability = async (
    attributesToMakeApplicable: UUID[],
    attributesToMakeUnapplicable: UUID[]
  ) => {
    const { data: partial } = await axios.put<PIMProductPartial>(
      `/v2/tenants/${tenant_id}/pim/products/${product.id}/groups/${resolvedGroup.id}/applicability`,
      {
        attributes_to_make_applicable: attributesToMakeApplicable,
        attributes_to_make_unapplicable: attributesToMakeUnapplicable,
      }
    );
    await applyPartialPimProductAndRefresh(mutateProduct, partial);
  };
  const submitGroupChanges = async (formValues: ProductGroupFormValues) => {
    try {
      setIsLoading(true);
      productStatusBeforeEditRef.current = productFromContext.status;
      const { values, values_to_delete } = buildAttributesPatchData(
        formValues,
        group_attributes_map
      );
      if (values.length === 0 && values_to_delete.length === 0) {
        notifySuccess(t("No changes to save"));
        setIsEditMode(false);
        return;
      }

      const { data: ackedProduct }: { data: PIMProductStatusAck } =
        await updateProductStatus();

      const { data: partial } = await axios.patch<PIMProductPartial>(
        `/v2/tenants/${tenant_id}/pim/products/${ackedProduct.id}/groups/${resolvedGroup.id}`,
        { values, values_to_delete }
      );

      const newDocumentAssetIds = values
        .filter(({ attribute_id }) =>
          isDocumentAttribute(group_attributes_map[attribute_id]?.input_type)
        )
        .map(({ value }) => (value as { asset_id?: string }).asset_id)
        .filter((id): id is string => Boolean(id));

      if (newDocumentAssetIds.length > 0) {
        await axios.patch(
          endpoints.v2_tenants_tenant_id_pim_products_product_id_assets_associate(
            tenant_id,
            ackedProduct.id
          ),
          { asset_ids: newDocumentAssetIds }
        );
      }

      const displayStatus =
        partial.display_status ?? ackedProduct.display_status;
      const transitionedToStaged =
        (displayStatus === "staged" ||
          displayStatus === "unpublished_staged") &&
        (productStatusBeforeEditRef.current === "published" ||
          productStatusBeforeEditRef.current === "unpublished");

      if (transitionedToStaged) {
        replaceProductId(
          partial.product_number ??
            partial.primary_staged_product_id ??
            partial.id ??
            ackedProduct.product_number ??
            ackedProduct.primary_staged_product_id ??
            ackedProduct.id
        );
      } else {
        await applyPartialPimProductAndRefresh(mutateProduct, partial);
      }
      notifySuccess(t("Your changes have been saved"));
      setIsEditMode(false);
    } catch (error) {
      const responseData = (error as AxiosError)?.response?.data;
      if (isAttributeEditRejected(responseData)) {
        notifyError(getAttributeEditRejectedMessage(responseData, t), {
          error,
        });
      } else {
        const errorMessage = (responseData as { message?: string })?.message;
        notifyError(
          errorMessage || t("There was an error saving your changes"),
          { error }
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (formValues: ProductGroupFormValues) => {
    if (hasObjectReferenceValueChanged(formValues)) {
      setPendingObjectRefConfirmation(formValues);
      return;
    }

    await submitGroupChanges(formValues);
  };

  const onCreateOption = async (value: string, attribute: AttributeSchema) => {
    if (!hasPermission("modify_lists")) {
      notifyError(
        t("You don't have permission to modify list {{list_name}}", {
          list_name: attribute.list_name,
        })
      );
      return;
    }
    try {
      const response = await axios.post<{ name: string; id: UUID }>(
        endpoints.v2_tenants_id_or_slug_pim_lists_id_items(
          tenant_id,
          attribute.list_id
        ),
        {
          name: value,
        }
      );
      if (attribute.input_type === "single_select") {
        methodsOfUseForm.setValue(attribute.id, {
          value: response.data.id,
          label: value,
        });
        revalidatePimProductDeferred(mutateProduct);
      }
      notifySuccess(
        t("{{value}} was saved to the {{list_name}} list", {
          value: value,
          list_name: attribute.list_name,
        })
      );
    } catch (error) {
      notifyError(
        t("There was an error adding {{value}} to the {{list_name}} list", {
          value: value,
          list_name: attribute.list_name,
        }),
        { error }
      );
    }
  };

  const onCreateImageAttr = async (
    image_attr: ImageAttributeValue | null,
    attribute: AttributeSchema
  ) => {
    methodsOfUseForm.setValue(attribute.id, image_attr);
  };

  const onCreateDocumentAttr = (
    docs: DocumentAttributeValue[],
    attribute: AttributeSchema
  ) => {
    methodsOfUseForm.setValue(attribute.id, docs);
  };

  return (
    <ProductGroupCardOuter className={PROFILE_DETAIL_CLASSES.groupCard}>
      <AccordionCardWithEditFeatures
        title={
          resolvedGroup.display_name
            ? t([resolvedGroup.display_name])
            : t(resolvedGroup.name)
        }
        handleEdit={
          !isPortfolio &&
          hasPermission("modify_products") &&
          hasPermission(`modify_${productTypePermissionSuffix}`) &&
          product.is_editable &&
          product.status !== "archived"
            ? () => setIsEditMode(!isEditMode)
            : undefined
        }
        handleChangeRequest={
          !isPortfolio &&
          isChangeRequestEnabled &&
          product.status !== "archived" &&
          !hasPermission(`modify_${productTypePermissionSuffix}`)
            ? () => setIsChangeRequest(true)
            : undefined
        }
        expanded={true}
        editing={isEditMode}
        editDisabled={resolvedGroup.attributes.every(
          (attr) => attr.is_editable === false
        )}
        editDisabledReason={
          resolvedGroup.attributes.every((attr) => attr.is_editable === false)
            ? t(
                "Your role doesn't have edit permission for any attributes in this group."
              )
            : undefined
        }
      >
        <ChangeRequestFormModal
          open={isChangeRequest}
          onClose={() => setIsChangeRequest(false)}
          variant={changeRequestContext.variant}
          groupData={changeRequestContext.groupData}
          onSubmit={onSubmitChangeRequest}
          submitting={isSubmitting}
          assigneePermissions={[`modify_${productTypePermissionSuffix}`]}
          assignee={product.product_owner}
        />

        {(!isEditMode || (isEditMode && editInModal)) && (
          <AttributeGroupDetails
            group={resolvedGroup}
            isPortfolio={isPortfolio}
            showMandatoryAsterisk={
              product.product_schema.show_mandatory_completion_score &&
              !isPortfolio
            }
            shouldHideEmptyAttributes={shouldHideEmptyAttributes}
            roleIsGuest={roleIsGuest}
            roleIsSomeKindOfBuyer={roleIsSomeKindOfBuyer}
            roleIsSomeKindOfSeller={roleIsSomeKindOfSeller}
          />
        )}
        {!isPortfolio &&
          !editInModal &&
          isEditMode &&
          product.status !== "archived" && (
            <EditFormWrapper>
              <AttributeGroupEditForm
                attributes={editableAttributes}
                onSubmit={onSubmit}
                onCancel={() => setIsEditMode(false)}
                methodsOfUseForm={methodsOfUseForm}
                onCreateOption={onCreateOption}
                onCreateImageAttribute={onCreateImageAttr}
                onCreateDocumentAttribute={onCreateDocumentAttr}
                product={product}
                isLoading={isLoading}
                updateApplicability={updateApplicability}
                showMandatoryAsterisk={
                  product.product_schema.show_mandatory_completion_score
                }
                pimProductType={product.pim_product_type}
                has_reference_entity_mapping={
                  resolvedGroup.has_reference_entity_mapping
                }
                group_id={resolvedGroup.id}
                objectSearchExcludeProfileId={product.id}
              />
            </EditFormWrapper>
          )}
        {!isPortfolio && editInModal && product.status !== "archived" && (
          <Modal
            overlay
            show={isEditMode && editInModal}
            closeModal={() => setIsEditMode(false)}
            modalWidth={"580px"}
          >
            <H4
              style={{
                fontSize: "var(--font_size_xl)",
                fontWeight: "var(--font_weight_semibold)",
              }}
            >
              {resolvedGroup.display_name}
            </H4>
            <EditFormWrapper editInModal={editInModal}>
              <AttributeGroupEditForm
                attributes={editableAttributes}
                onSubmit={onSubmit}
                onCancel={() => setIsEditMode(false)}
                methodsOfUseForm={methodsOfUseForm}
                onCreateOption={onCreateOption}
                onCreateImageAttribute={onCreateImageAttr}
                onCreateDocumentAttribute={onCreateDocumentAttr}
                product={product}
                isLoading={isLoading}
                updateApplicability={updateApplicability}
                showMandatoryAsterisk={
                  product.product_schema.show_mandatory_completion_score
                }
                pimProductType={product.pim_product_type}
                group_id={resolvedGroup.id}
                has_reference_entity_mapping={
                  resolvedGroup.has_reference_entity_mapping
                }
                objectSearchExcludeProfileId={product.id}
              />
            </EditFormWrapper>
          </Modal>
        )}
        {pendingObjectRefConfirmation && (
          <ObjectReferenceChangeConfirmDialog
            show={true}
            onClose={() => setPendingObjectRefConfirmation(null)}
            onConfirm={async () => {
              const pendingValues = pendingObjectRefConfirmation;
              setPendingObjectRefConfirmation(null);
              if (pendingValues) {
                await submitGroupChanges(pendingValues);
              }
            }}
          />
        )}
      </AccordionCardWithEditFeatures>
    </ProductGroupCardOuter>
  );
};
